import {
  ensureArray,
  isHexColor,
  isIsoString,
  isPlainObject,
  isValidDateString
} from './deckHelpers';
import { isCodeBearingKey, placeholderPattern } from './placeholders';

/**
 * Block types a saved deck may contain.
 *
 * This is not documentation — `repairDeck` rewrites any block whose type is
 * missing from this list to `text`, silently, on the way OUT of the app. A type
 * the app can create and render but that is absent here is therefore destroyed
 * by the next save, and the author only discovers it on reload. Adding a block
 * type to the renderer means adding it here in the same change.
 *
 * (`src/utils/templateValidation.js` keeps a separate, narrower list for
 * validating hand-authored template files. The two are deliberately different:
 * that one gates what an import may introduce, this one gates what the app's
 * own decks may keep.)
 */
export const ALLOWED_BLOCK_TYPES = [
  'audio',
  'text',
  'cards',
  'stacked-cards',
  'multi-column-cards',
  'project-timeline',
  'accordion',
  'apa-quote',
  'apa-citation',
  'apa-reference-list',
  'embedded-html',
  'custom-html',
  'image_drawover',
  'info-box',
  'warning-box',
  'exercise-box',
  'html',
  'image',
  'link',
  'worksheet',
  'headline',
  'heading',
  'divider',
  'list',
  'gallery',
  'video',
  'youtube-playlist'
];

const REQUIRED_TOP_LEVEL_FIELDS = [
  'format',
  'version',
  'schema_type',
  'timestamp',
  'courseTopic',
  'instructorName',
  'instructorEmail',
  'footerCourseInfo',
  'footerInstitution',
  'footerCopyright',
  'showHeaderBar',
  'schoolLogo',
  'schoolLogoName',
  'schoolLogoSize',
  'week',
  'date',
  'bodyLayout',
  'slideHeaderMode',
  'slideHeaderColor',
  'slideHeaderColors',
  'slideGlobalCss',
  'titleCardSettings',
  'endTitleCardSettings',
  'slideVisibility',
  'slideNames',
  'slideDeckName',
  'exportFileName',
  'htmlExportTitle',
  'exportVersion',
  'sections'
];

const addIssue = (list, path, code, message) => {
  list.push({ path, code, message });
};

const validatePlaceholderFree = (value, path, errors) => {
  if (typeof value !== 'string') return;
  const match = value.match(placeholderPattern());
  if (match) {
    addIssue(errors, path, 'PLACEHOLDER_FOUND', `Placeholder detected: ${match[0]}`);
  }
};

const validateBlockArrays = (block, blockPath, errors) => {
  ['items', 'cardItems', 'stackedCardItems', 'accordionItems', 'questions'].forEach((field) => {
    if (!Array.isArray(block[field])) {
      addIssue(errors, `${blockPath}.${field}`, 'BLOCK_ARRAY_MISSING', `${field} must be an array.`);
    }
  });
};

const validateBlockContentByType = (block, blockPath, errors) => {
  const type = block.type;
  if (['text', 'heading', 'headline', 'info-box', 'warning-box', 'exercise-box', 'list'].includes(type)) {
    if (typeof block.content !== 'string') {
      addIssue(errors, `${blockPath}.content`, 'CONTENT_INVALID', 'Content must be an HTML string.');
    }
  }
  if (type === 'divider') {
    if (!isPlainObject(block.content)) {
      addIssue(errors, `${blockPath}.content`, 'DIVIDER_CONTENT_INVALID', 'Divider content must be an object.');
    }
  }
  if (type === 'cards' && !Array.isArray(block.items)) {
    addIssue(errors, `${blockPath}.items`, 'CARDS_ITEMS_INVALID', 'Cards items must be an array.');
  }
  if (type === 'stacked-cards' && !Array.isArray(block.items)) {
    addIssue(errors, `${blockPath}.items`, 'STACKED_ITEMS_INVALID', 'Stacked cards items must be an array.');
  }
  if (type === 'multi-column-cards') {
    if (!Array.isArray(block.columns)) {
      addIssue(errors, `${blockPath}.columns`, 'COLUMNS_INVALID', 'Columns must be an array.');
    } else {
      block.columns.forEach((column, columnIndex) => {
        if (!column || typeof column !== 'object') {
          addIssue(errors, `${blockPath}.columns[${columnIndex}]`, 'COLUMN_INVALID', 'Column must be an object.');
          return;
        }
        if (!column.width || !column.widthPercent) {
          addIssue(errors, `${blockPath}.columns[${columnIndex}]`, 'COLUMN_WIDTH_MISSING', 'Column width and widthPercent are required.');
        }
      });
    }
  }
  if (type === 'accordion' && !Array.isArray(block.accordionItems)) {
    addIssue(errors, `${blockPath}.accordionItems`, 'ACCORDION_ITEMS_INVALID', 'Accordion items must be an array.');
  }
  if (type === 'apa-citation' && !Array.isArray(block.citations)) {
    addIssue(errors, `${blockPath}.citations`, 'APA_CITATION_INVALID', 'APA citation blocks must include a citations array.');
  }
  if (type === 'apa-reference-list' && !Array.isArray(block.citations)) {
    addIssue(errors, `${blockPath}.citations`, 'APA_REFERENCE_INVALID', 'APA reference list blocks must include a citations array.');
  }
  if (type === 'worksheet' && !Array.isArray(block.questions)) {
    addIssue(errors, `${blockPath}.questions`, 'WORKSHEET_QUESTIONS_INVALID', 'Worksheet questions must be an array.');
  }
  if (type === 'gallery' && !Array.isArray(block.items) && !Array.isArray(block.images)) {
    addIssue(errors, `${blockPath}.items`, 'GALLERY_ITEMS_INVALID', 'Gallery items must be an array.');
  }
};

const collectIds = (block, blockPath, ids, errors) => {
  const registerId = (value, path) => {
    if (!value || typeof value !== 'string') {
      addIssue(errors, path, 'ID_MISSING', 'Id must be a non-empty string.');
      return;
    }
    if (ids.has(value)) {
      addIssue(errors, path, 'ID_DUPLICATE', `Duplicate id found: ${value}`);
    } else {
      ids.add(value);
    }
  };

  registerId(block.id, `${blockPath}.id`);
  ensureArray(block.columns).forEach((column, columnIndex) => {
    if (!column) return;
    registerId(column.id, `${blockPath}.columns[${columnIndex}].id`);
    ensureArray(column.cards).forEach((card, cardIndex) => {
      if (!card) return;
      registerId(card.id, `${blockPath}.columns[${columnIndex}].cards[${cardIndex}].id`);
      ensureArray(card.checklistItems).forEach((item, itemIndex) => {
        if (!item) return;
        registerId(item.id, `${blockPath}.columns[${columnIndex}].cards[${cardIndex}].checklistItems[${itemIndex}].id`);
      });
      ensureArray(card.numberedList).forEach((item, itemIndex) => {
        if (!item) return;
        registerId(item.id, `${blockPath}.columns[${columnIndex}].cards[${cardIndex}].numberedList[${itemIndex}].id`);
      });
    });
  });
  ensureArray(block.items).forEach((item, itemIndex) => {
    if (!item) return;
    registerId(item.id, `${blockPath}.items[${itemIndex}].id`);
  });
  ensureArray(block.accordionItems).forEach((item, itemIndex) => {
    if (!item) return;
    registerId(item.id, `${blockPath}.accordionItems[${itemIndex}].id`);
  });
  ensureArray(block.questions).forEach((item, itemIndex) => {
    if (!item) return;
    registerId(item.id, `${blockPath}.questions[${itemIndex}].id`);
  });
  ensureArray(block.images).forEach((item, itemIndex) => {
    if (!item) return;
    registerId(item.id, `${blockPath}.images[${itemIndex}].id`);
  });
  ensureArray(block.cardItems).forEach((item, itemIndex) => {
    if (!item) return;
    registerId(item.id, `${blockPath}.cardItems[${itemIndex}].id`);
  });
  ensureArray(block.stackedCardItems).forEach((item, itemIndex) => {
    if (!item) return;
    registerId(item.id, `${blockPath}.stackedCardItems[${itemIndex}].id`);
  });
};

const traverseForPlaceholders = (value, path, errors) => {
  if (typeof value === 'string') {
    validatePlaceholderFree(value, path, errors);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => traverseForPlaceholders(item, `${path}[${index}]`, errors));
    return;
  }
  if (isPlainObject(value)) {
    Object.entries(value).forEach(([key, next]) => {
      // Skipped for the same reason repairDeck skips them: brackets in a
      // stylesheet are selectors, not unfilled placeholders. Flagging one here
      // would fail a perfectly good deck that the repairer is right to leave
      // alone.
      if (isCodeBearingKey(key)) return;
      traverseForPlaceholders(next, `${path}.${key}`, errors);
    });
  }
};

export const validateDeck = (deck) => {
  const errors = [];
  const warnings = [];

  if (!deck || typeof deck !== 'object') {
    addIssue(errors, 'root', 'INVALID_ROOT', 'Deck must be an object.');
    return { ok: false, errors, warnings };
  }

  REQUIRED_TOP_LEVEL_FIELDS.forEach((field) => {
    if (!(field in deck)) {
      addIssue(errors, field, 'FIELD_MISSING', `Missing required field: ${field}`);
    }
  });

  if (deck.format !== 'boppps_lesson') {
    addIssue(errors, 'format', 'FORMAT_INVALID', 'format must be "boppps_lesson".');
  }
  if (deck.version !== '4.0.0') {
    addIssue(errors, 'version', 'VERSION_MISMATCH', 'version must be "4.0.0".');
  }
  if (deck.schema_type !== 'full_deck') {
    addIssue(errors, 'schema_type', 'SCHEMA_TYPE_INVALID', 'schema_type must be "full_deck".');
  }
  if (!isIsoString(deck.timestamp)) {
    addIssue(errors, 'timestamp', 'TIMESTAMP_INVALID', 'timestamp must be an ISO string.');
  }
  if (deck.showHeaderBar !== true) {
    addIssue(errors, 'showHeaderBar', 'SHOW_HEADER_BAR_INVALID', 'showHeaderBar must be true.');
  }
  if (deck.schoolLogo !== null && typeof deck.schoolLogo !== 'string') {
    addIssue(errors, 'schoolLogo', 'SCHOOL_LOGO_INVALID', 'schoolLogo must be null or base64 string.');
  }
  if (typeof deck.schoolLogoName !== 'string') {
    addIssue(errors, 'schoolLogoName', 'SCHOOL_LOGO_NAME_INVALID', 'schoolLogoName must be a string.');
  }
  if (typeof deck.schoolLogoSize !== 'number') {
    addIssue(errors, 'schoolLogoSize', 'SCHOOL_LOGO_SIZE_INVALID', 'schoolLogoSize must be a number.');
  }
  if (typeof deck.week !== 'string') {
    addIssue(errors, 'week', 'WEEK_INVALID', 'week must be a string.');
  }
  if (!isValidDateString(deck.date)) {
    addIssue(errors, 'date', 'DATE_INVALID', 'date must be formatted as YYYY-MM-DD.');
  }
  if (deck.bodyLayout !== 'slides') {
    addIssue(errors, 'bodyLayout', 'BODY_LAYOUT_INVALID', 'bodyLayout must be "slides".');
  }
  if (!['single', 'per-section'].includes(deck.slideHeaderMode)) {
    addIssue(errors, 'slideHeaderMode', 'SLIDE_HEADER_MODE_INVALID', 'slideHeaderMode must be "single" or "per-section".');
  }
  if (!isHexColor(deck.slideHeaderColor)) {
    addIssue(errors, 'slideHeaderColor', 'SLIDE_HEADER_COLOR_INVALID', 'slideHeaderColor must be a hex color string.');
  }
  if (!isPlainObject(deck.slideHeaderColors)) {
    addIssue(errors, 'slideHeaderColors', 'SLIDE_HEADER_COLORS_INVALID', 'slideHeaderColors must be an object.');
  }
  if (typeof deck.slideGlobalCss !== 'string') {
    addIssue(errors, 'slideGlobalCss', 'SLIDE_GLOBAL_CSS_INVALID', 'slideGlobalCss must be a string.');
  }
  ['titleCardSettings', 'endTitleCardSettings'].forEach((field) => {
    if (!isPlainObject(deck[field])) {
      addIssue(errors, field, 'TITLE_CARD_INVALID', `${field} must be an object.`);
    } else {
      if (typeof deck[field].title !== 'string') {
        addIssue(errors, `${field}.title`, 'TITLE_CARD_TITLE_INVALID', `${field}.title must be a string.`);
      }
      if (typeof deck[field].subtitle !== 'string') {
        addIssue(errors, `${field}.subtitle`, 'TITLE_CARD_SUBTITLE_INVALID', `${field}.subtitle must be a string.`);
      }
    }
  });

  if (!isPlainObject(deck.slideVisibility)) {
    addIssue(errors, 'slideVisibility', 'SLIDE_VISIBILITY_INVALID', 'slideVisibility must be an object.');
  } else {
    Object.entries(deck.slideVisibility).forEach(([key, value]) => {
      if (typeof value !== 'boolean') {
        addIssue(errors, `slideVisibility.${key}`, 'SLIDE_VISIBILITY_VALUE_INVALID', 'slideVisibility values must be boolean.');
      }
    });
  }
  if (!isPlainObject(deck.slideNames)) {
    addIssue(errors, 'slideNames', 'SLIDE_NAMES_INVALID', 'slideNames must be an object.');
  } else {
    Object.entries(deck.slideNames).forEach(([key, value]) => {
      if (typeof value !== 'string') {
        addIssue(errors, `slideNames.${key}`, 'SLIDE_NAME_VALUE_INVALID', 'slideNames values must be strings.');
      }
    });
  }

  const sections = deck.sections;
  if (!isPlainObject(sections)) {
    addIssue(errors, 'sections', 'SECTIONS_INVALID', 'sections must be an object.');
  } else {
    const slideIds = new Set();
    const ids = new Set();
    Object.entries(sections).forEach(([sectionKey, sectionValue]) => {
      if (!sectionValue || typeof sectionValue !== 'object') {
        addIssue(errors, `sections.${sectionKey}`, 'SECTION_INVALID', 'Section must be an object.');
        return;
      }
      if (!sectionValue.id) {
        addIssue(errors, `sections.${sectionKey}.id`, 'SECTION_ID_MISSING', 'Section id is required.');
      }
      if (!sectionValue.title) {
        addIssue(errors, `sections.${sectionKey}.title`, 'SECTION_TITLE_MISSING', 'Section title is required.');
      }
      const blocks = sectionValue.blocks;
      if (!Array.isArray(blocks)) {
        addIssue(errors, `sections.${sectionKey}.blocks`, 'BLOCKS_INVALID', 'Blocks must be an array.');
        return;
      }
      blocks.forEach((block, blockIndex) => {
        const blockPath = `sections.${sectionKey}.blocks[${blockIndex}]`;
        if (!block || typeof block !== 'object') {
          addIssue(errors, blockPath, 'BLOCK_INVALID', 'Block must be an object.');
          return;
        }
        if (!block.id) {
          addIssue(errors, `${blockPath}.id`, 'BLOCK_ID_MISSING', 'Block id is required.');
        }
        if (!block.slideId || typeof block.slideId !== 'string') {
          addIssue(errors, `${blockPath}.slideId`, 'BLOCK_SLIDE_ID_MISSING', 'slideId is required.');
        } else {
          slideIds.add(block.slideId);
        }
        if (!block.type || typeof block.type !== 'string') {
          addIssue(errors, `${blockPath}.type`, 'BLOCK_TYPE_MISSING', 'Block type is required.');
        } else if (!ALLOWED_BLOCK_TYPES.includes(block.type)) {
          addIssue(errors, `${blockPath}.type`, 'BLOCK_TYPE_INVALID', `Unsupported block type: ${block.type}`);
        }
        validateBlockArrays(block, blockPath, errors);
        validateBlockContentByType(block, blockPath, errors);
        collectIds(block, blockPath, ids, errors);
      });
    });

    slideIds.forEach((slideId) => {
      if (!deck.slideVisibility || !(slideId in deck.slideVisibility)) {
        addIssue(errors, `slideVisibility.${slideId}`, 'SLIDE_VISIBILITY_MISSING', `Missing slideVisibility for ${slideId}.`);
      }
      if (!deck.slideNames || !(slideId in deck.slideNames)) {
        addIssue(errors, `slideNames.${slideId}`, 'SLIDE_NAME_MISSING', `Missing slideNames entry for ${slideId}.`);
      }
    });
  }

  traverseForPlaceholders(deck, 'root', errors);

  return { ok: errors.length === 0, errors, warnings };
};
